import React, { useLayoutEffect, useMemo, useRef } from 'react';

import { useCompany } from '@hooks/queries/useCompany';
import { AuthManager } from '@store/AuthManager';
import { ZendeskIframeManager } from '@store/ZendeskIframeManager';
import { useIsMobile } from '@styles';
import { getConfig } from '@utils/config';
import { observer } from 'mobx-react';
import { stringify } from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

export interface ZendeskIframeProps {}

export const ZendeskIframe: React.FC = observer(() => {
  const location = useLocation();
  const history = useHistory();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const manager = useMemo(() => ZendeskIframeManager.shared, []);

  const company = useCompany(undefined, {
    enabled: AuthManager.shared.isLoggedIn && !location.pathname.startsWith('/external'),
  });

  const shouldDisplay =
    !location.pathname.startsWith('/admin') &&
    !location.pathname.startsWith('/auth') &&
    !location.pathname.startsWith('/Maintenance') &&
    !location.pathname.startsWith('/external') &&
    !!AuthManager.shared.key &&
    company.data &&
    company.data.status === '手続き完了' &&
    getConfig().zendeskEnabled;

  const isMobile = useIsMobile();
  const initialIsMobile = useMemo(() => isMobile, []); // Avoid trigger this when printing...

  useLayoutEffect(() => {
    if (shouldDisplay) {
      const iframe = iframeRef.current!;

      manager.setContentWindow(iframe.contentWindow!);
      manager.setNavigateToHandler((path) => history.push(path));

      function handleMessage(ev: MessageEvent<any>) {
        if (ev.origin === getConfig().d2dFrontendZendeskUrl && typeof ev.data === 'object') {
          manager.handleMessage(ev.data);
        }
      }

      window.addEventListener('message', handleMessage);

      return () => window.removeEventListener('message', handleMessage);
    }
  }, [shouldDisplay]);

  if (!shouldDisplay) return null;

  return (
    <iframe
      ref={iframeRef}
      style={{
        position: 'fixed',
        left: isMobile ? 16 : 60,
        bottom: isMobile ? 16 : 60,
        zIndex: 10000,
        border: 'none',
        visibility: shouldDisplay ? 'initial' : 'hidden',
        filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
        transition: 'height 0.1s, width 0.1s',
        ...(isMobile &&
          manager.open && {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 9999999,
          }),
      }}
      height={manager.open ? (isMobile ? undefined : 600) : 90}
      width={manager.open ? (isMobile ? undefined : 500) : 110}
      /**
       * Pass necessary auth info to Zendesk iframe.
       * We don't pass key when auth_type is `Bearer` (for jwt)
       * because `AuthManager.shared.key` will be updated frequently
       * which leads to unwanted reloading of the iframe.
       */
      src={`${getConfig().d2dFrontendZendeskUrl}?${stringify({
        key: AuthManager.shared.auth_type === 'Token' ? AuthManager.shared.key : undefined,
        refresh: AuthManager.shared.refresh ?? undefined,
        auth_type: AuthManager.shared.auth_type,
        isMobile: initialIsMobile,
      })}`}
    />
  );
});
